import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Star,
  TrendingUp,
  Brain,
  Heart,
  Activity,
  Users,
  Target,
  Zap,
  Shield,
  Calendar,
  Save,
  History
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Rating selector component - MUST be outside main component to prevent re-renders
const RatingSelector = ({ value, onChange, label, description }) => (
  <div className="space-y-3">
    <div>
      <p className="text-base font-medium text-white">{label}</p>
      {description && <p className="text-sm text-zinc-500 mt-1">{description}</p>}
    </div>
    <div className="flex justify-between gap-2">
      {[1, 2, 3, 4, 5].map((num) => (
        <button
          key={num}
          type="button"
          onClick={() => onChange(num)}
          className={`flex-1 h-14 rounded-lg font-bold text-lg transition-all active:scale-95 ${
            value === num 
              ? 'bg-[#CCFF00] text-black' 
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
          }`}
        >
          {num}
        </button>
      ))}
    </div>
    <div className="flex justify-between text-xs text-zinc-400 px-1">
      <span>Malo</span>
      <span>Excelente</span>
    </div>
  </div>
);

// Text question component - MUST be outside main component to prevent re-renders
const TextQuestion = ({ value, onChange, label, placeholder, required = false }) => (
  <div className="space-y-2">
    <label className="text-base font-medium text-white">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <Textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="min-h-[120px] bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-400 text-base resize-none"
    />
  </div>
);

function MonthlyCheckIn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [previousCheckIns, setPreviousCheckIns] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  const totalSteps = 6;
  
  // Section titles for progress
  const sections = [
    { step: 1, title: 'Rendimiento', icon: TrendingUp },
    { step: 2, title: 'Mental', icon: Brain },
    { step: 3, title: 'Físico', icon: Activity },
    { step: 4, title: 'Entorno', icon: Users },
    { step: 5, title: 'Objetivos', icon: Target },
    { step: 6, title: 'Resumen', icon: Check },
  ];

  const [formData, setFormData] = useState({
    month: new Date().toISOString().slice(0, 7),
    general_rating: 3,
    best_of_month: '',
    most_difficult: '',
    valued_by_staff: '',
    satisfied_with_minutes: '',
    doing_well: '',
    needs_improvement: '',
    frustration_management: '',
    moments_of_insecurity: '',
    confidence_sources: '',
    physical_feeling: '',
    discomforts_alerts: '',
    rest_recovery: '',
    relationship_staff: '',
    relationship_teammates: '',
    environment_impact: '',
    next_month_goal: '',
    habit_to_improve: '',
    needs_from_agency: ''
  });

  useEffect(() => {
    if (user?.role === 'player') {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    try {
      // Load previous check-ins
      const prevRes = await api.get('/monthly-checkins');
      setPreviousCheckIns(prevRes.data);
      
      // Try to load current month's draft
      const currentMonth = new Date().toISOString().slice(0, 7);
      try {
        const currentRes = await api.get(`/monthly-checkins/${currentMonth}`);
        setFormData(currentRes.data);
        toast.info('Continuando tu check-in del mes');
      } catch (e) {
        // No draft exists, check localStorage
        const savedDraft = localStorage.getItem(`checkin_draft_${currentMonth}`);
        if (savedDraft) {
          setFormData(JSON.parse(savedDraft));
          toast.info('Borrador recuperado');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-save draft to localStorage
  const saveDraft = useCallback(() => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    localStorage.setItem(`checkin_draft_${currentMonth}`, JSON.stringify(formData));
    setLastSaved(new Date());
  }, [formData]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(saveDraft, 30000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  // Save draft when changing steps
  useEffect(() => {
    if (!loading) saveDraft();
  }, [step, loading, saveDraft]);

  const handleNext = () => {
    saveDraft();
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    saveDraft();
    if (step > 1) setStep(step - 1);
    else navigate('/dashboard');
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.post('/monthly-checkins', formData);
      // Clear draft
      const currentMonth = new Date().toISOString().slice(0, 7);
      localStorage.removeItem(`checkin_draft_${currentMonth}`);
      toast.success('Check-in mensual guardado');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const loadPreviousCheckIn = async (month) => {
    try {
      const res = await api.get(`/monthly-checkins/${month}`);
      setFormData(res.data);
      setShowHistory(false);
      setStep(1);
      toast.success('Check-in cargado');
    } catch (error) {
      toast.error('Error al cargar');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // History view
  if (showHistory) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#050505] px-4 py-6 pb-24">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => setShowHistory(false)} className="p-2 -ml-2 text-zinc-400">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Oswald' }}>
                HISTORIAL
              </h1>
            </div>

            {previousCheckIns.length === 0 ? (
              <div className="card-dark p-8 text-center">
                <History className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
                <p className="text-zinc-400">No hay check-ins anteriores</p>
              </div>
            ) : (
              <div className="space-y-3">
                {previousCheckIns.map((checkIn) => (
                  <button
                    key={checkIn.month}
                    onClick={() => loadPreviousCheckIn(checkIn.month)}
                    className="w-full card-dark p-4 flex items-center justify-between active:scale-98 transition-transform"
                  >
                    <div className="text-left">
                      <p className="text-white font-medium">
                        {new Date(checkIn.month + '-01').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                      </p>
                      <p className="text-sm text-zinc-500">
                        Rating: {checkIn.general_rating}/5
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < checkIn.general_rating ? 'text-[#CCFF00] fill-[#CCFF00]' : 'text-zinc-500'}`}
                        />
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#050505]">
        {/* Header */}
        <div className="sticky top-14 lg:top-0 z-30 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 px-4 py-3">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-3">
              <button onClick={handleBack} className="p-2 -ml-2 text-zinc-400 active:scale-95">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="text-center">
                <span className="text-xs text-zinc-500 uppercase tracking-wider">Check-in Mensual</span>
                <p className="text-sm text-white font-medium">{sections[step - 1]?.title}</p>
              </div>
              <button 
                onClick={() => setShowHistory(true)} 
                className="p-2 -mr-2 text-zinc-400 active:scale-95"
              >
                <History className="w-5 h-5" />
              </button>
            </div>
            
            {/* Section tabs */}
            <div className="flex gap-1 overflow-x-auto scrollbar-hide -mx-4 px-4">
              {sections.map((section) => {
                const Icon = section.icon;
                const isActive = step === section.step;
                const isCompleted = step > section.step;
                return (
                  <button
                    key={section.step}
                    onClick={() => setStep(section.step)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full whitespace-nowrap transition-all ${
                      isActive 
                        ? 'bg-[#CCFF00] text-black' 
                        : isCompleted
                          ? 'bg-zinc-800 text-[#CCFF00]'
                          : 'bg-zinc-900 text-zinc-500'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-medium">{section.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Auto-save indicator */}
        {lastSaved && (
          <div className="px-4 pt-4 max-w-lg mx-auto">
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <Save className="w-3 h-3" />
              <span>Guardado: {lastSaved.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-4 py-6 max-w-lg mx-auto pb-32">
          
          {/* Step 1: Rendimiento */}
          {step === 1 && (
            <div className="space-y-8 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald' }}>
                  RENDIMIENTO DEPORTIVO
                </h1>
                <p className="text-zinc-500">¿Cómo ha sido tu mes a nivel deportivo?</p>
              </div>

              <RatingSelector
                value={formData.general_rating}
                onChange={(v) => setFormData({ ...formData, general_rating: v })}
                label="Valoración general del mes"
                description="Considerando todos los aspectos de tu rendimiento"
              />

              <TextQuestion
                value={formData.best_of_month}
                onChange={(v) => setFormData({ ...formData, best_of_month: v })}
                label="¿Qué fue lo mejor del mes?"
                placeholder="Momentos destacados, logros, sensaciones positivas..."
              />

              <TextQuestion
                value={formData.most_difficult}
                onChange={(v) => setFormData({ ...formData, most_difficult: v })}
                label="¿Qué fue lo más difícil?"
                placeholder="Obstáculos, desafíos, momentos complicados..."
              />

              <TextQuestion
                value={formData.valued_by_staff}
                onChange={(v) => setFormData({ ...formData, valued_by_staff: v })}
                label="¿Te sientes valorado por el cuerpo técnico?"
                placeholder="Describe cómo percibes tu relación con el staff..."
              />

              <TextQuestion
                value={formData.satisfied_with_minutes}
                onChange={(v) => setFormData({ ...formData, satisfied_with_minutes: v })}
                label="¿Satisfecho con tus minutos de juego?"
                placeholder="¿Cuánto has jugado? ¿Es lo que esperabas?"
              />
            </div>
          )}

          {/* Step 2: Mental */}
          {step === 2 && (
            <div className="space-y-8 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald' }}>
                  ESTADO MENTAL
                </h1>
                <p className="text-zinc-500">Tu bienestar psicológico este mes</p>
              </div>

              <TextQuestion
                value={formData.doing_well}
                onChange={(v) => setFormData({ ...formData, doing_well: v })}
                label="¿Qué estás haciendo bien?"
                placeholder="Aspectos mentales en los que destacas..."
              />

              <TextQuestion
                value={formData.needs_improvement}
                onChange={(v) => setFormData({ ...formData, needs_improvement: v })}
                label="¿Qué necesitas mejorar?"
                placeholder="Áreas de crecimiento mental..."
              />

              <TextQuestion
                value={formData.frustration_management}
                onChange={(v) => setFormData({ ...formData, frustration_management: v })}
                label="¿Cómo gestionas la frustración?"
                placeholder="Cuando las cosas no salen bien, ¿qué haces?"
              />

              <TextQuestion
                value={formData.moments_of_insecurity}
                onChange={(v) => setFormData({ ...formData, moments_of_insecurity: v })}
                label="¿Has tenido momentos de inseguridad?"
                placeholder="Situaciones donde dudaste de ti mismo..."
              />

              <TextQuestion
                value={formData.confidence_sources}
                onChange={(v) => setFormData({ ...formData, confidence_sources: v })}
                label="¿De dónde sacas tu confianza?"
                placeholder="¿Qué te da seguridad en ti mismo?"
              />
            </div>
          )}

          {/* Step 3: Físico */}
          {step === 3 && (
            <div className="space-y-8 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald' }}>
                  ESTADO FÍSICO
                </h1>
                <p className="text-zinc-500">Tu cuerpo y recuperación</p>
              </div>

              <TextQuestion
                value={formData.physical_feeling}
                onChange={(v) => setFormData({ ...formData, physical_feeling: v })}
                label="¿Cómo te sientes físicamente?"
                placeholder="Energía, fuerza, resistencia general..."
              />

              <TextQuestion
                value={formData.discomforts_alerts}
                onChange={(v) => setFormData({ ...formData, discomforts_alerts: v })}
                label="¿Molestias o alertas físicas?"
                placeholder="Dolores, sobrecargas, zonas sensibles..."
              />

              <TextQuestion
                value={formData.rest_recovery}
                onChange={(v) => setFormData({ ...formData, rest_recovery: v })}
                label="¿Cómo está tu descanso y recuperación?"
                placeholder="Calidad de sueño, rutinas de recuperación..."
              />
            </div>
          )}

          {/* Step 4: Entorno */}
          {step === 4 && (
            <div className="space-y-8 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald' }}>
                  ENTORNO
                </h1>
                <p className="text-zinc-500">Relaciones y ambiente</p>
              </div>

              <TextQuestion
                value={formData.relationship_staff}
                onChange={(v) => setFormData({ ...formData, relationship_staff: v })}
                label="Relación con el cuerpo técnico"
                placeholder="¿Cómo es tu comunicación con entrenadores?"
              />

              <TextQuestion
                value={formData.relationship_teammates}
                onChange={(v) => setFormData({ ...formData, relationship_teammates: v })}
                label="Relación con compañeros"
                placeholder="¿Cómo te sientes en el grupo?"
              />

              <TextQuestion
                value={formData.environment_impact}
                onChange={(v) => setFormData({ ...formData, environment_impact: v })}
                label="¿Cómo te afecta el entorno?"
                placeholder="Familia, amigos, ciudad, club..."
              />
            </div>
          )}

          {/* Step 5: Objetivos */}
          {step === 5 && (
            <div className="space-y-8 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald' }}>
                  OBJETIVOS
                </h1>
                <p className="text-zinc-500">Mirando hacia adelante</p>
              </div>

              <TextQuestion
                value={formData.next_month_goal}
                onChange={(v) => setFormData({ ...formData, next_month_goal: v })}
                label="Objetivo principal del próximo mes"
                placeholder="¿Qué quieres lograr?"
                required
              />

              <TextQuestion
                value={formData.habit_to_improve}
                onChange={(v) => setFormData({ ...formData, habit_to_improve: v })}
                label="Hábito a mejorar"
                placeholder="Un hábito específico que quieras trabajar..."
              />

              <TextQuestion
                value={formData.needs_from_agency}
                onChange={(v) => setFormData({ ...formData, needs_from_agency: v })}
                label="¿Qué necesitas de la agencia?"
                placeholder="¿En qué podemos ayudarte?"
              />
            </div>
          )}

          {/* Step 6: Resumen */}
          {step === 6 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald' }}>
                  RESUMEN
                </h1>
                <p className="text-zinc-500">Revisa tu check-in antes de enviar</p>
              </div>

              {/* Rating display */}
              <div className="card-dark p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-500">Valoración del mes</p>
                    <div className="flex items-center gap-2 mt-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-6 h-6 ${i < formData.general_rating ? 'text-[#CCFF00] fill-[#CCFF00]' : 'text-zinc-500'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Oswald' }}>
                      {formData.general_rating}
                    </p>
                    <p className="text-xs text-zinc-500">de 5</p>
                  </div>
                </div>
              </div>

              {/* Quick summary */}
              <div className="space-y-4">
                {formData.best_of_month && (
                  <div className="card-dark p-4">
                    <p className="text-xs text-[#CCFF00] uppercase tracking-wider mb-1">Lo mejor</p>
                    <p className="text-white text-sm">{formData.best_of_month}</p>
                  </div>
                )}
                
                {formData.next_month_goal && (
                  <div className="card-dark p-4">
                    <p className="text-xs text-blue-400 uppercase tracking-wider mb-1">Objetivo próximo mes</p>
                    <p className="text-white text-sm">{formData.next_month_goal}</p>
                  </div>
                )}

                {formData.needs_from_agency && (
                  <div className="card-dark p-4">
                    <p className="text-xs text-purple-400 uppercase tracking-wider mb-1">Necesitas de Ikons</p>
                    <p className="text-white text-sm">{formData.needs_from_agency}</p>
                  </div>
                )}
              </div>

              {/* Sections completed */}
              <div className="card-dark p-4">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Secciones completadas</p>
                <div className="grid grid-cols-5 gap-2">
                  {sections.slice(0, 5).map((section) => {
                    const Icon = section.icon;
                    return (
                      <div key={section.step} className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-full bg-[#CCFF00]/20 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#CCFF00]" />
                        </div>
                        <span className="text-[10px] text-zinc-500 mt-1">{section.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Bottom Action */}
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 p-4 bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
          <div className="max-w-lg mx-auto flex gap-3">
            {step > 1 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="h-14 px-6 border-zinc-700 text-white hover:bg-zinc-800"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            
            {step < totalSteps ? (
              <Button
                onClick={handleNext}
                className="flex-1 h-14 bg-[#CCFF00] hover:bg-[#b8e600] text-black font-bold text-base uppercase tracking-wide active:scale-95 transition-transform"
              >
                Continuar
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="flex-1 h-14 bg-[#CCFF00] hover:bg-[#b8e600] text-black font-bold text-base uppercase tracking-wide active:scale-95 transition-transform"
              >
                {saving ? 'Guardando...' : 'Enviar Check-in'}
                <Check className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default MonthlyCheckIn;
